
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useState } from "react";
import { COLORS, StarIcon } from "../Token";
import { InductionFeedback } from "../Types";

export const LOW_RATING_THRESHOLD = 3;

interface FeedbackFormProps {
  submitting: boolean;
  onSubmit: (feedback: InductionFeedback) => void;
}

export function FeedbackForm({
  submitting,
  onSubmit,
}: FeedbackFormProps) {
  const [rating, setRating] = useState(0);
  const [suggestion, setSuggestion] = useState("");
  const [detail, setDetail] = useState("");

  const isLow = rating > 0 && rating < LOW_RATING_THRESHOLD;

  return (
    <Flex direction="column" gap={3} maxW="680px" w="100%">
      {/* Rating */}
      <Box role="group" aria-labelledby="rating-label">
        <Text
          id="rating-label"
          fontSize="13px"
          fontWeight={600}
          mb={1.5}
          color="#243548"
        >
          Overall rating
        </Text>

        <Flex gap={1.5}>
          {[1, 2, 3, 4, 5].map((n) => (
            <Button
              key={n}
              onClick={() => setRating(n)}
              aria-label={`Rate ${n} ${n === 1 ? "star" : "stars"}`}
              aria-pressed={n <= rating}
              w="32px"
              h="32px"
              minW="32px"
              p={0}
              borderRadius="9px"
              border="1px solid #CFE5D8"
              bg="#FBFDFC"
              _hover={{ bg: "#F1F8F4" }}
            >
              <StarIcon boxSize={5} filled={n <= rating} />
            </Button>
          ))}
        </Flex>
      </Box>

      {/* Low rating acknowledgement */}
      {isLow && (
        <Alert
          status="info"
          borderRadius="9px"
          py={2}
          px={3}
          alignItems="center"
        >
          <AlertIcon boxSize={4} />
          <Text fontSize="12px" lineHeight="1.4">
            Thank you for being honest. Your feedback has been noted
            and HR will review it.
          </Text>
        </Alert>
      )}

      {/* Suggestions */}
      <FormControl>
        <FormLabel
          fontSize="12px"
          fontWeight={600}
          mb={1}
          color="#243548"
        >
          Improvement suggestions
        </FormLabel>

        <Input
          value={suggestion}
          onChange={(e) => setSuggestion(e.target.value)}
          placeholder="One thing we could do better"
          h="36px"
          fontSize="12px"
          px={3}
          borderRadius="9px"
          borderColor="#B9D3C5"
          focusBorderColor={COLORS.brand}
        />
      </FormControl>

      {/* Detailed feedback */}
      <FormControl>
        <FormLabel
          fontSize="12px"
          fontWeight={600}
          mb={1}
          color="#243548"
        >
          Detailed feedback
        </FormLabel>

        <Textarea
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          placeholder="Tell us about your induction experience"
          rows={2}
          minH="58px"
          maxH="70px"
          resize="none"
          fontSize="12px"
          lineHeight="1.5"
          p={2.5}
          borderRadius="9px"
          borderColor="#B9D3C5"
          focusBorderColor={COLORS.brand}
        />
      </FormControl>

      {/* Submit */}
      <Flex align="center" gap={3} wrap="wrap">
        <Button
          onClick={() => onSubmit({ rating, suggestion, detail })}
          isDisabled={rating === 0}
          isLoading={submitting}
          loadingText="Submitting"
          minH="38px"
          h="38px"
          px={5}
          borderRadius="9px"
          bg={COLORS.brand}
          color="white"
          fontSize="13px"
          _hover={{
            bg: COLORS.brandDark,
            _disabled: { bg: COLORS.brand },
          }}
        >
          Complete induction
        </Button>

        {rating === 0 && (
          <Text fontSize="11px" color={COLORS.inkSoft}>
            Choose a rating to finish.
          </Text>
        )}
      </Flex>
    </Flex>
  );
}
